import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthResponse, LoginInput, PublicUser, RegisterInput } from '@harbor/shared';
import { apiRequest, onSessionExpired, setAccessToken } from '../lib/api.js';

/**
 * Authentication state.
 *
 * The session is restored on mount by calling `POST /api/auth/refresh`. That
 * request is expected to fail for a first-time visitor (no cookie), and a
 * failure is not an error condition — it just means "signed out". Treating it as
 * an error would show a red banner to every new visitor.
 *
 * `status` is a three-state value rather than a boolean on purpose. `loading`
 * and `signed-out` need to render differently: the first is a skeleton, the
 * second is a sign-in prompt. Collapsing them into `user === null` produces the
 * familiar flash of "Sign in" for a user who is in fact signed in.
 */

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  user: PublicUser | null;
  status: Status;
  signIn: (input: LoginInput) => Promise<void>;
  signUp: (input: RegisterInput) => Promise<void>;
  signOut: () => Promise<void>;
  signOutEverywhere: () => Promise<void>;
  isStaff: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  const applySession = useCallback((response: AuthResponse) => {
    setAccessToken(response.accessToken);
    setUser(response.user);
    setStatus('authenticated');
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  // Restore an existing session from the refresh cookie.
  useEffect(() => {
    let active = true;

    apiRequest<AuthResponse>('/api/auth/refresh', { method: 'POST' })
      .then((response) => {
        if (active) applySession(response);
      })
      .catch(() => {
        if (active) clearSession();
      });

    return () => {
      active = false;
    };
  }, [applySession, clearSession]);

  // The API client tells us when a refresh has definitively failed mid-session.
  useEffect(() => onSessionExpired(clearSession), [clearSession]);

  const signIn = useCallback(
    async (input: LoginInput) => {
      applySession(await apiRequest<AuthResponse>('/api/auth/login', { method: 'POST', body: input }));
    },
    [applySession],
  );

  const signUp = useCallback(
    async (input: RegisterInput) => {
      applySession(await apiRequest<AuthResponse>('/api/auth/register', { method: 'POST', body: input }));
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } finally {
      // Even if the request fails, the local session must go. Leaving a user
      // "signed in" because the network was down is worse than a stale cookie.
      clearSession();
    }
  }, [clearSession]);

  const signOutEverywhere = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout-all', { method: 'POST' });
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      signIn,
      signUp,
      signOut,
      signOutEverywhere,
      isStaff: user?.role === 'manager' || user?.role === 'admin',
    }),
    [user, status, signIn, signUp, signOut, signOutEverywhere],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
